<script setup lang="ts">
/**
 * BrowseView — Center panel for public fittings results
 *
 * Reads browse state from the Pinia store (filters are in the left panel FittingsBrowser).
 * Shows fit cards in a scrollable list with pagination.
 */
import { useFitStore } from '../../stores/fitStore';
import { iconUrl } from '../../composables/useApi';

const store = useFitStore();

const emit = defineEmits<{
  'select-fit': [fitID: number];
}>();

function formatCost(cost: number | null): string {
  if (!cost) return '';
  if (cost >= 1_000_000_000) return `${(cost / 1_000_000_000).toFixed(1)}B ISK`;
  if (cost >= 1_000_000) return `${(cost / 1_000_000).toFixed(1)}M ISK`;
  if (cost >= 1_000) return `${(cost / 1_000).toFixed(0)}K ISK`;
  return `${cost.toFixed(0)} ISK`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}
</script>

<template>
  <div class="browse-view">
    <div class="browse-header">
      <h2 class="browse-title">Public Fittings</h2>
      <span v-if="!store.browseLoading && store.browseResponse" class="browse-count">
        {{ store.browseTotalFits }} fitting{{ store.browseTotalFits !== 1 ? 's' : '' }}
      </span>
    </div>

    <!-- Results -->
    <div class="browse-grid">
      <div v-if="store.browseLoading" class="browse-loading">Loading fittings...</div>
      <div v-else-if="store.browseFitsList.length === 0" class="browse-empty">
        No public fittings found. Try adjusting your filters in the left panel.
      </div>

      <button
        v-for="fit in store.browseFitsList"
        :key="fit.fitID"
        class="fit-tile"
        @click="emit('select-fit', fit.fitID)"
      >
        <img
          :src="iconUrl(fit.shipTypeID, 64)"
          :alt="fit.shipName"
          class="tile-icon"
          loading="lazy"
        />
        <div class="tile-body">
          <span class="tile-name">{{ fit.fitName }}</span>
          <span class="tile-ship">{{ fit.shipName }}</span>
          <div class="tile-meta">
            <span v-if="fit.estimated_cost" class="tile-cost">{{ formatCost(fit.estimated_cost) }}</span>
            <span class="tile-date">{{ timeAgo(fit.updatedAt) }}</span>
          </div>
        </div>
      </button>
    </div>

    <!-- Pagination -->
    <div v-if="store.browseTotalPages > 1" class="browse-pagination">
      <button class="page-btn" :disabled="store.browsePage <= 1" @click="store.browseGoToPage(store.browsePage - 1)">&laquo; Prev</button>
      <span class="page-info">Page {{ store.browsePage }} of {{ store.browseTotalPages }}</span>
      <button class="page-btn" :disabled="store.browsePage >= store.browseTotalPages" @click="store.browseGoToPage(store.browsePage + 1)">Next &raquo;</button>
    </div>
  </div>
</template>

<style scoped>
.browse-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.browse-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.browse-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.browse-count {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

/* Tile grid */
.browse-grid {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
  align-content: start;
}

.browse-loading,
.browse-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.9rem;
  padding: 40px 0;
  grid-column: 1 / -1;
}

.fit-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  background: var(--bg-panel);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: center;
}

.fit-tile:hover {
  border-color: var(--accent-blue);
  background: var(--bg-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.tile-icon {
  width: 64px;
  height: 64px;
  border-radius: 6px;
}

.tile-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 0;
}

.tile-name {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile-ship {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.tile-meta {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 2px;
}

.tile-cost {
  font-size: 0.75rem;
  color: var(--accent-green);
}

.tile-date {
  font-size: 0.7rem;
  color: var(--text-secondary);
}

/* Pagination */
.browse-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-color);
  flex-shrink: 0;
}

.page-btn {
  padding: 6px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: var(--bg-panel);
  color: var(--text-primary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.page-btn:hover:not(:disabled) {
  border-color: var(--accent-blue);
  background: var(--bg-hover);
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  font-size: 0.85rem;
  color: var(--text-secondary);
}
</style>
